import React, { useState, useEffect, createRef } from "react";
import {
  Box,
  Button,
  getColor,
  Icons,
  Modal,
  Tooltip,
  Typography,
  useToast,
} from "fds";
import classNames from "classnames";
import "./index.scss";
import utilsDocs from "../../../../shared/utils/docs";
import Dropzone, { FileRejection, FileWithPath } from "react-dropzone";
import useDocumentStore from "../../../../redux/dispatcher/useDocumentStore";
import { nanoid } from "@reduxjs/toolkit";
import { UploadFile, UploadProgress } from "../../../../types/files";
import { getUserInfoFromLocalStorage } from "../../../../shared/utils/auth";
import { LoginDto } from "../../../../types/auth";
import utilsCommon from "../../../../shared/utils/common";
import { t } from "i18next";
import { useNavigate } from "react-router-dom";
interface FileUploadModalProps {
  classes?: string;
  open: boolean;
  modalType?: "SHARE_TO_WORKGROUP" | "SHARE_TO_CHILD_WORKGROUP";
  workgroupId?: string;
  type?: "DROPBOX" | "default";
  onClose: () => void;
  onNext?: (files: UploadFile[], uid: string) => void;
}

const FileUploadModal: React.FC<FileUploadModalProps> = ({
  open,
  workgroupId,
  modalType,
  type = "default",
  onClose,
  onNext,
  classes,
}) => {
  let dropzoneRef: any;
  const user = getUserInfoFromLocalStorage() as string | "";
  const loginDto: LoginDto = JSON.parse(user);

  /** Hook 정리 */
  const toasts = useToast();
  const {
    documentState,
    getDocUploadUidCreate,
    setAddUploadProgressFiles,
    setUpdateUploadProgressFiles,
    doDocUploadFile,
    removeFileFromUid,
    doDocUploadFileComplete,
    recallGetContentList,
  } = useDocumentStore();
  const navigate = useNavigate();

  /**State 정리 */
  const [uid, changeUid] = useState(""); // 파일 업로드시 사용할 uid
  const [uploadFiles, changeUploadFiles] =
    useState<(File & { uuid: string })[]>(); // 업로드할 파일 리스트 타입이 File임
  const [progressPercent, setProgressPercent] = useState<number>(0); // ?
  const [deleteUUidConfirm, changeDeleteUuidConfirm] = useState<
    number | undefined
  >(undefined); // 파일이 한개 남았고, 해당 파일을 제거하려고 할때 사용하는 제거 확인 모달
  const [isDrive, setIsDrive] = useState(
    documentState.uploadDocsProgress &&
      documentState.uploadDocsProgress.uploadFiles[0]?.fileType &&
      ["GOOGLE", "DROPBOX"].includes(
        documentState.uploadDocsProgress?.uploadFiles[0]?.fileType
      )
  );
  /** UseEffectd 정리 */
  useEffect(() => {
    if (open) {
      if (type === "default") {
        setUpdateUploadProgressFiles({} as UploadFile);
        getDocUploadUidCreate().then((item: any) => changeUid(item.payload));
      } else {
        changeUid(documentState.uid);
      }
    }
  }, [open]);
  useEffect(() => {
    const handleUploadFile = async (targetFile: UploadFile): Promise<void> => {
      if (!targetFile.file) return;
      const uploadFile: UploadFile = {
        uuid: targetFile?.uuid,
        fileName: targetFile?.fileName ?? "",
        fileSize: targetFile?.fileSize ?? 0,
        workgroupId: workgroupId ? workgroupId : "",
        workgroupName: workgroupId ? workgroupId : "",
        status: "ING", // WAIT, ING, SUCCESS, FAIL
        errorMsg: "",
        file: targetFile?.file,
        relativePath: (targetFile as any)?.relativePath,
      };
      setUpdateUploadProgressFiles(uploadFile);
      const uploadResp = await doDocUploadFile(
        uid,
        targetFile?.file,
        setProgressPercent
      );
      if (uploadResp.payload) {
        setUpdateUploadProgressFiles({
          uuid: targetFile?.uuid,
          fileName: targetFile?.fileName ?? "",
          fileSize: targetFile?.fileSize ?? 0,
          workgroupId: workgroupId ? workgroupId : "",
          workgroupName: workgroupId ? workgroupId : "",
          status: uploadResp.payload.error ? "FAIL" : "SUCCESS", // WAIT, ING, SUCCESS, FAIL
          errorMsg: uploadResp.payload.error?.message ?? "",
          fileId: uploadResp.payload?.fileId ?? "",
          relativePath: (targetFile as any)?.relativePath,
        });
      }
    };
    if (
      documentState.uploadDocsProgress?.uploadFiles?.length === 0 ||
      documentState.uploadDocsProgress?.uploadFiles.filter(
        (item: any) => item.status === "ING"
      )?.length > 0
    )
      return;
    const targetFile = documentState.uploadDocsProgress?.uploadFiles.filter(
      (item: any) => item.status === "WAIT"
    );
    if (!targetFile || targetFile.length === 0) return;
    handleUploadFile(targetFile[0]);
  }, [documentState.uploadDocsProgress]);

  /** 함수 정리 */
  /** 파일 업로드 상태 확인을 위한 드롭 */
  const onDrop = (
    acceptedFiles: File[],
    fileRejections: FileRejection[],
    e: any
  ): void => {
    e.preventDefault();
    e.stopPropagation();
    if (isDrive) return;
    const uuid = nanoid();
    const newFileList = (
      uploadFiles ? [...uploadFiles, ...acceptedFiles] : [...acceptedFiles]
    ).map((item, i) =>
      "uuid" in item
        ? (item as File & { uuid: string })
        : ({
            ...item,
            uuid: uuid + i,
          } as File & { uuid: string })
    );

    changeUploadFiles(newFileList);
    setProgressPercent(0);
    /** upload Progress 값에 저장하기 */
    for (let i = 0; i < acceptedFiles.length; i++) {
      const file: FileWithPath = acceptedFiles[i];
      const uploadFile: UploadFile = {
        isMultiple: true,
        uuid: uuid + i,
        fileName: file.name,
        fileSize: file.size,
        workgroupId: workgroupId ?? "",
        workgroupName: workgroupId ?? "",
        status: "WAIT", // WAIT, ING, SUCCESS, FAIL
        errorMsg: "",
        file: acceptedFiles[i],
        relativePath: (acceptedFiles[i] as any)?.relativePath,
      };
      setAddUploadProgressFiles(uploadFile);
    }
    /** 업로드 하기 전 파일 체크 */
    for (let i = 0; i < acceptedFiles.length; i++) {
      const file: File = acceptedFiles[i];
      const fileExt = file.name.split(".")[file.name.split(".").length - 1];
      let errorMsg = "";
      if (
        !fileExt ||
        loginDto.serviceConfig.disableExtensions.includes(fileExt)
      ) {
        errorMsg = t("file.alert.notSupportedExt");
      } else if (file.size === 0) {
        errorMsg = t("file.alert.notAllowZeroSize");
      } else if (
        file.size >
        loginDto.ecoCommonConfig.fileUploadMaxSizeMB * 1024 * 1024
      ) {
        errorMsg = t("file.alert.notAllowMaxSize", {
          num: loginDto.ecoCommonConfig.fileUploadMaxSizeMB,
        });
      } else if (
        documentState.uploadDocsProgress?.uploadFiles.filter(
          (item1: UploadFile) =>
            (file as any)?.relativePath === item1?.relativePath
        ) &&
        documentState.uploadDocsProgress?.uploadFiles.filter(
          (item1: UploadFile) =>
            (file as any)?.relativePath === item1?.relativePath
        ).length > 0
      ) {
        errorMsg = t("file.alert.duplicateFile");
      }
      if (errorMsg !== "") {
        const uploadFile: UploadFile = {
          uuid: uuid + i,
          fileName: file.name,
          fileSize: file.size,
          workgroupId: workgroupId ?? "",
          workgroupName: workgroupId ?? "",
          status: "FAIL", // WAIT, ING, SUCCESS, FAIL
          errorMsg,
          relativePath: (acceptedFiles[i] as any)?.relativePath,
        };
        setUpdateUploadProgressFiles(uploadFile);
        continue;
      }
    }
  };
  /** 파일 최종 업로드 */
  const handleUploadDocumentComplete = async () => {
    const urlParmas = new URLSearchParams(window.location.search);
    const givenFolderId = urlParmas.get("folderId");

    let completeResp: any = await doDocUploadFileComplete(uid, {
      workgroupId: workgroupId ?? "",
      permissionList: [{ changeCode: "CREATE", targetType: "WORKGROUP" }],
      folderId: givenFolderId && givenFolderId !== "ROOT" ? givenFolderId : "",
      isMultiple: true,
    });
    if (completeResp?.payload?.error || completeResp?.error) {
      toasts.toastMsg(
        nanoid(),
        completeResp?.payload?.error?.message ??
          completeResp?.error?.message ??
          t("common.alert.internalError"),
        "error"
      );
      setUpdateUploadProgressFiles({} as UploadFile);
      onClose();
    } else {
      toasts.toastMsg(nanoid(), t("file.progress.finishInfo"), "success");
      if (window.location.href.includes(`/workgroup/${workgroupId}/file`))
        recallGetContentList();
      setUpdateUploadProgressFiles({} as UploadFile);
      onClose();
    }
    if (!window.location.href.includes(`/workgroup/${workgroupId}/file`)) {
      navigate(
        utilsCommon.renderProperNavigatePath(`/workgroup/${workgroupId}/file`)
      );
    }
  };
  return (
    <Modal
      title={
        workgroupId || !!modalType
          ? t("file.action.chooseFile")
          : t("sendingFile.chooseFile")
      }
      open={open}
      onClose={() => {
        setUpdateUploadProgressFiles({} as UploadFile);
        onClose();
      }}
      width={540}
      classes={"manualUpload"}
      footer={
        <Box width="100%" alignItems="center">
          {utilsCommon.checkUploadFilePermission(loginDto) &&
            type === "default" && (
              <Button
                btnStyle="link"
                label={t("common.upload")}
                style={{ marginLeft: 0 }}
                leadingIcon={<Icons variant="line" label="upload" />}
                onClick={(e) => {
                  e.preventDefault();
                  dropzoneRef?.open();
                }}
                classes="mg mr-auto"
              />
            )}
          <Box style={{ marginInlineStart: "auto" }}>
            <Button
              btnStyle="fill"
              label={t("common.cancel")}
              type="secondary"
              onClick={() => {
                setUpdateUploadProgressFiles({} as UploadFile);
                onClose();
              }}
            />
            <Button
              btnStyle="fill"
              label={onNext ? t("common.next") : t("file.share")}
              classes="mg ml-8"
              state={
                documentState.uploadDocsProgress?.uploadFiles.length === 0 ||
                documentState.uploadDocsProgress?.uploadFiles.filter(
                  (item: any) => item.status !== "SUCCESS"
                ).length > 0
                  ? "disable"
                  : "default"
              }
              onClick={() =>
                onNext
                  ? onNext(documentState.uploadDocsProgress?.uploadFiles, uid)
                  : handleUploadDocumentComplete()
              }
            />
          </Box>
        </Box>
      }
      body={
        <Box width="100%" direction="column">
          {!!deleteUUidConfirm && (
            <Modal
              title={t("file.fileDelete")}
              width={540}
              footer={
                <Box justifyContent="flex-end">
                  <Button
                    btnStyle="fill"
                    type="secondary"
                    onClick={() => changeDeleteUuidConfirm(undefined)}
                    label={t("common.cancel")}
                  />
                  <Button
                    btnStyle="fill"
                    classes="mg ml-4"
                    onClick={() => {
                      changeDeleteUuidConfirm(undefined);
                      setUpdateUploadProgressFiles({} as UploadFile);
                      onClose();
                    }}
                    label={t("common.delete")}
                  />
                </Box>
              }
              open={!!deleteUUidConfirm}
              onClose={() => {
                setUpdateUploadProgressFiles({} as UploadFile);
                changeDeleteUuidConfirm(undefined);
              }}
              bodyMsg={t("file.alert.deleteLastConfirm")}
            ></Modal>
          )}
          <Box alignItems="center" classes="mg mb-8">
            <Typography type="subtitle2">
              {workgroupId || modalType === "SHARE_TO_WORKGROUP"
                ? t("file.action.sharefiles")
                : t("sendingFile.selectedFiles")}
            </Typography>
            {documentState.uploadDocsProgress?.uploadFiles?.length > 0 && (
              <Typography
                classes="mg ml-4"
                type="subtitle2"
                exactColor={getColor("COLOR_TEXT_BRAND")}
              >
                {documentState.uploadDocsProgress?.uploadFiles?.length ?? ""}
              </Typography>
            )}
          </Box>
          <Dropzone
            noClick
            noKeyboard
            ref={(node) => {
              if (node) dropzoneRef = node;
            }}
            disabled={
              !utilsCommon.checkUploadFilePermission(loginDto) || isDrive
            }
            onDrop={onDrop}
          >
            {({ getRootProps, getInputProps, isDragActive, inputRef }) => (
              <div
                {...getRootProps({ classNames: "dropzone" })}
                className={classNames("dropzone", {
                  accept: isDragActive,
                  false: !isDragActive,
                })}
                style={{ width: 484, height: 352 }}
              >
                <input {...getInputProps()} />
                <Box classes="dropzone-info">
                  <Box
                    classes={classNames("dropzone-dragging", {
                      accept: isDragActive,
                    })}
                    style={{ display: isDragActive ? "flex" : "none" }}
                  >
                    <img
                      src={
                        window.eco.isCspp
                          ? require("../../../../shared/images/pages/File/emptyFilterDoc.png")
                          : require("../../../../shared/images/pages/File/UploadFile.png")
                      }
                      alt={"UploadFile"}
                      height={110}
                    />
                    <Typography
                      type="h3"
                      textAlign="center"
                      exactColor={getColor("COLOR_BG_PRIMARY")}
                      classes="mg mt-20"
                    >
                      {t("file.dropHere")}
                    </Typography>
                  </Box>
                  {!documentState.uploadDocsProgress ||
                  documentState.uploadDocsProgress?.uploadFiles?.length ===
                    0 ? (
                    <Box
                      direction="column"
                      alignItems="center"
                      onClick={() => (inputRef = dropzoneRef)}
                    >
                      <img
                        src={
                          window.eco.isCspp
                            ? require("../../../../shared/images/pages/File/emptyFilterDoc.png")
                            : require("../../../../shared/images/pages/File/UploadFile.png")
                        }
                        alt={"UploadFile"}
                        width={200}
                        height={100}
                      />
                      <Typography
                        type="body2_rg"
                        width={256}
                        textAlign="center"
                      >
                        {t("file.uploadInstruction")}
                      </Typography>
                    </Box>
                  ) : (
                    <Box
                      direction="column"
                      width={"100%"}
                      height={"100%"}
                      classes={"dropzone-fileList"}
                    >
                      {documentState?.uploadDocsProgress?.uploadFiles?.map(
                        (item: any) => {
                          const isSupportExt = [
                            ...new Set([
                              ...(loginDto?.serviceConfig?.revisionExtensions ||
                                []),
                              ...(loginDto?.serviceConfig
                                ?.manualRevisionExtensions || []),
                            ]),
                          ].includes(item.fileName?.split(".")?.pop() ?? "");

                          return (
                            <Box
                              alignItems="center"
                              classes={classNames("dropzone-fileItem", {
                                failed: item.status === "FAIL",
                                success: item.status === "SUCCESS",
                              })}
                            >
                              {utilsDocs.renderDocIcon(item.fileName, 32)}
                              <Box
                                direction="column"
                                classes="mg ml-8"
                                maxWidth={"calc(100% - 100px)"}
                              >
                                <Tooltip message={item.fileName}>
                                  <Typography type="body2_rg" ellipsis>
                                    {item.fileName}
                                  </Typography>
                                </Tooltip>
                                <Typography
                                  type="body4_rg"
                                  exactColor={getColor("COLOR_TEXT_SECONDARY")}
                                >
                                  {utilsDocs.fileSizeFormat(item.fileSize)}
                                </Typography>
                              </Box>
                              <Box classes="mg ml-auto">
                                <Tooltip
                                  message={
                                    item.errorMsg
                                      ? item.errorMsg
                                      : t("file.noWrappingFileInclude")
                                  }
                                  hideToolTip={
                                    item.errorMsg === "" && isSupportExt
                                  }
                                >
                                  {utilsDocs.renderDocProgressIcon(
                                    item.status,
                                    isSupportExt
                                  )}
                                </Tooltip>
                                {["SUCCESS", "FAIL"].includes(item.status) && (
                                  <Box
                                    classes="cursor pointer mg ml-12"
                                    onClick={() => {
                                      documentState?.uploadDocsProgress
                                        ?.uploadFiles.length === 1 && isDrive
                                        ? changeDeleteUuidConfirm(item.uuid)
                                        : removeFileFromUid(
                                            uid,
                                            item.uuid,
                                            item.fileId
                                          );
                                      changeUploadFiles(
                                        uploadFiles?.filter(
                                          (item1) => item1.uuid !== item.uuid
                                        )
                                      );
                                    }}
                                  >
                                    <Icons variant="line" label="delete" />
                                  </Box>
                                )}
                              </Box>
                            </Box>
                          );
                        }
                      )}
                    </Box>
                  )}
                </Box>
              </div>
            )}
          </Dropzone>
        </Box>
      }
    />
  );
};
export default FileUploadModal;
